import type {
  EnrichRule,
  IngestionStepOptions,
  LinkNodesStep,
  WorkflowBuilderState,
  WorkflowEntity,
  WorkflowMetrics,
  WorkflowRelationship,
  WorkflowRelationshipMapping,
  UnstructuredRelationship,
} from '@/interfaces/workflows.interface';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { useProjectContext } from './ProjectContext';
import {
  workflowInitialState,
  workflowsReducer,
} from './reducers/WorkflowReducer';
import {
  linkingDefaultModel,
  embeddingDefaultModel,
  getDefaultAgentTypeForFileType,
  getDefaults,
  extractionAgentTypeFromApi,
  EMBEDDINGS_API_TYPES,
  LINKING_AGENT_TYPES,
  SERVICES,
} from '@/config/models.config';
import { getEntityUpdatesForRelationshipMappingIndexes, getEntityUpdatesToClearIndexWhenMappingRemoved, getEntityUpdatesForLinkNodesConfigurationIndexes } from '@/helpers/workflows.helpers';

interface WorkflowsContextType {
  state: WorkflowBuilderState;
  isReadonly: boolean;
  addEntity: (entity: WorkflowEntity) => void;
  updateEntity: (entityId: string, updates: Partial<WorkflowEntity>) => void;
  removeEntity: (entityId: string) => void;
  addRelationship: (relationship: WorkflowRelationship) => void;
  updateRelationship: (
    relationshipId: string,
    updates: Partial<WorkflowRelationship>
  ) => void;
  removeRelationship: (relationshipId: string) => void;
  addRelationshipMapping: (mapping: WorkflowRelationshipMapping) => void;
  updateRelationshipMapping: (
    mappingId: string,
    updates: Partial<WorkflowRelationshipMapping>
  ) => void;
  removeRelationshipMapping: (mappingId: string) => void;
  addUnstructuredRelationship: (relationship: UnstructuredRelationship) => void;
  updateUnstructuredRelationship: (
    relationshipId: string,
    updates: Partial<UnstructuredRelationship>
  ) => void;
  removeUnstructuredRelationship: (relationshipId: string) => void;
  addLinkNodesConfiguration: (configuration: LinkNodesStep) => void;
  updateLinkNodesConfiguration: (
    configurationId: string,
    updates: Partial<LinkNodesStep>
  ) => void;
  removeLinkNodesConfiguration: (configurationId: string) => void;
  addEnrichRule: (enrichRule: EnrichRule) => void;
  updateEnrichRule: (enrichRuleIndex: number, enrichRule: EnrichRule) => void;
  removeEnrichRule: (enrichRuleIndex: number) => void;
  setCurrentStep: (step: number) => void;
  resetWorkflow: () => void;
  loadWorkflow: (workflow: WorkflowBuilderState) => void;
  getMetrics: () => WorkflowMetrics;
  updateStepOptions: (stepOptions: {
    structured?: IngestionStepOptions;
    unstructured?: IngestionStepOptions;
  }) => void;
  generateWorkflowConfig: () => any;
  setIsReadonly: (isReadonly: boolean) => void;
}

const WorkflowsContext = createContext<WorkflowsContextType | undefined>(
  undefined
);

export const useWorkflowsContext = () => {
  const context = useContext(WorkflowsContext);
  if (!context) {
    throw new Error(
      'useWorkflowsContext must be used within a WorkflowsProvider'
    );
  }
  return context;
};

interface WorkflowsProviderProps {
  children: ReactNode;
}

export const WorkflowsProvider: React.FC<WorkflowsProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(workflowsReducer, workflowInitialState);
  const [isReadonly, setIsReadonlyState] = React.useState(false);
  const { selectedProjectName } = useProjectContext();

  useEffect(() => {
    if (state.entities.length > 0) {
      generateWorkflowConfig();
    }
  }, [
    state.entities,
    state.relationships,
    state.relationshipMappings,
    state.unstructuredRelationships,
    state.enrichRules,
    state.stepOptions,
  ]);

  const addEntity = (entity: WorkflowEntity) => {
    dispatch({ type: 'ADD_ENTITY', payload: entity });
  };

  const updateEntity = (entityId: string, updates: Partial<WorkflowEntity>) => {
    dispatch({ type: 'UPDATE_ENTITY', payload: { entityId, updates } });
  };

  const removeEntity = (entityId: string) => {
    dispatch({ type: 'REMOVE_ENTITY', payload: entityId });
  };

  const addRelationship = (relationship: WorkflowRelationship) => {
    dispatch({ type: 'ADD_RELATIONSHIP', payload: relationship });
  };

  const updateRelationship = (
    relationshipId: string,
    updates: Partial<WorkflowRelationship>
  ) => {
    dispatch({
      type: 'UPDATE_RELATIONSHIP',
      payload: { relationshipId, updates },
    });
  };

  const removeRelationship = (relationshipId: string) => {
    dispatch({ type: 'REMOVE_RELATIONSHIP', payload: relationshipId });
  };

  const addRelationshipMapping = (mapping: WorkflowRelationshipMapping) => {
    dispatch({ type: 'ADD_RELATIONSHIP_MAPPING', payload: mapping });
    getEntityUpdatesForRelationshipMappingIndexes(mapping, state.entities).forEach(
      ({ entityId, updates }) => {
        dispatch({ type: 'UPDATE_ENTITY', payload: { entityId, updates } });
      }
    );
  };

  const updateRelationshipMapping = (
    mappingId: string,
    updates: Partial<WorkflowRelationshipMapping>
  ) => {
    dispatch({
      type: 'UPDATE_RELATIONSHIP_MAPPING',
      payload: { mappingId, updates },
    });
    const current = state.relationshipMappings.find((m) => m.id === mappingId);
    if (current) {
      const merged: WorkflowRelationshipMapping = { ...current, ...updates };
      getEntityUpdatesForRelationshipMappingIndexes(merged, state.entities).forEach(
        ({ entityId, updates: entityUpdates }) => {
          dispatch({
            type: 'UPDATE_ENTITY',
            payload: { entityId, updates: entityUpdates },
          });
        }
      );
    }
  };

  const removeRelationshipMapping = (mappingId: string) => {
    const removed = state.relationshipMappings.find((m) => m.id === mappingId);
    dispatch({ type: 'REMOVE_RELATIONSHIP_MAPPING', payload: mappingId });
    if (removed) {
      const remaining = state.relationshipMappings.filter((m) => m.id !== mappingId);
      getEntityUpdatesToClearIndexWhenMappingRemoved(
        removed,
        remaining,
        state.entities
      ).forEach(({ entityId, updates }) => {
        dispatch({ type: 'UPDATE_ENTITY', payload: { entityId, updates } });
      });
    }
  };

  const addUnstructuredRelationship = (
    relationship: UnstructuredRelationship
  ) => {
    dispatch({ type: 'ADD_UNSTRUCTURED_RELATIONSHIP', payload: relationship });
  };

  const updateUnstructuredRelationship = (
    relationshipId: string,
    updates: Partial<UnstructuredRelationship>
  ) => {
    dispatch({
      type: 'UPDATE_UNSTRUCTURED_RELATIONSHIP',
      payload: { relationshipId, updates },
    });
  };

  const removeUnstructuredRelationship = (relationshipId: string) => {
    dispatch({
      type: 'REMOVE_UNSTRUCTURED_RELATIONSHIP',
      payload: relationshipId,
    });
  };

  const addLinkNodesConfiguration = (configuration: LinkNodesStep) => {
    dispatch({ type: 'ADD_LINK_NODES_CONFIGURATION', payload: configuration });
    getEntityUpdatesForLinkNodesConfigurationIndexes(
      configuration,
      state.entities
    ).forEach(({ entityId, updates }) => {
      dispatch({ type: 'UPDATE_ENTITY', payload: { entityId, updates } });
    });
  };

  const updateLinkNodesConfiguration = (
    configurationId: string,
    updates: Partial<LinkNodesStep>
  ) => {
    dispatch({
      type: 'UPDATE_LINK_NODES_CONFIGURATION',
      payload: { configurationId, updates },
    });
    const current = state.linkNodesConfigurations.find(
      (c) => c.id === configurationId
    );
    if (current) {
      const merged: LinkNodesStep = { ...current, ...updates };
      getEntityUpdatesForLinkNodesConfigurationIndexes(
        merged,
        state.entities
      ).forEach(({ entityId, updates: entityUpdates }) => {
        dispatch({
          type: 'UPDATE_ENTITY',
          payload: { entityId, updates: entityUpdates },
        });
      });
    }
  };

  const removeLinkNodesConfiguration = (configurationId: string) => {
    dispatch({
      type: 'REMOVE_LINK_NODES_CONFIGURATION',
      payload: configurationId,
    });
  };

  const addEnrichRule = (enrichRule: EnrichRule) => {
    dispatch({ type: 'ADD_ENRICH_RULE', payload: enrichRule });
  };

  const updateEnrichRule = (enrichRuleIndex: number, enrichRule: EnrichRule) => {
    dispatch({
      type: 'UPDATE_ENRICH_RULE',
      payload: { index: enrichRuleIndex, rule: enrichRule },
    });
  };

  const removeEnrichRule = (enrichRuleIndex: number) => {
    dispatch({ type: 'REMOVE_ENRICH_RULE', payload: enrichRuleIndex });
  };

  const setCurrentStep = (step: number) => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: step });
  };

  const resetWorkflow = () => {
    dispatch({ type: 'RESET_WORKFLOW' });
  };

  const loadWorkflow = (workflow: WorkflowBuilderState) => {
    dispatch({ type: 'LOAD_WORKFLOW', payload: workflow });
  };

  const getMetrics = (): WorkflowMetrics => {
    const totalProperties = state.entities.reduce(
      (total, entity) => total + entity.properties.length,
      0
    );

    // Calculate enrich entities count
    const enrichEntitiesCount = state.enrichRules.reduce((total, rule) => {
      if (
        rule.type === 'AskAIExtractEntities' &&
        rule.labels &&
        rule.labels.length > 0
      ) {
        return total + rule.labels.length;
      }
      return total;
    }, 0);

    // Calculate enrich relationships count
    const enrichRelationshipsCount = state.enrichRules.reduce((total, rule) => {
      if (
        rule.type === 'AskAIExtractEntities' &&
        rule.labels &&
        rule.labels.length > 0 &&
        rule.relationName &&
        rule.graphFilter.labelFilters.length > 0
      ) {
        return total + rule.labels.length;
      }
      return total;
    }, 0);

    return {
      filesUploaded: 0, // No files in this context
      entitiesCreated: state.entities.length,
      propertiesMapped: totalProperties,
      relationshipsConfigured:
        state.relationshipMappings.length +
        state.unstructuredRelationships.length +
        state.linkNodesConfigurations.length,
      enrichEntitiesCreated: enrichEntitiesCount,
      enrichRelationshipsDefined: enrichRelationshipsCount,
    };
  };

  const updateStepOptions = (newStepOptions: Record<string, any>) => {
    dispatch({ type: 'UPDATE_STEP_OPTIONS', payload: newStepOptions });
  };

  // Function to sort pipeline strategies in the correct order
  const sortPipelineStrategies = (strategies: any[]) => {
    const strategyOrder = [
      'exact_match',
      'jaccard_similarity',
      'embedding_similarity',
      'llm',
    ];
    // Create a new array instead of mutating the original
    return [...strategies].sort((a, b) => {
      const aIndex = strategyOrder.indexOf(a.method);
      const bIndex = strategyOrder.indexOf(b.method);
      return aIndex - bIndex;
    });
  };

  const generateWorkflowConfig = () => {
    // Helper function to convert static values based on type
    const convertStaticValue = (type: string, value: string | undefined) => {
      if (!value) return value;

      if (type === 'int' || type === 'float') {
        return +value; // Convert to number
      } else if (type === 'boolean') {
        return value === 'true' || value === '1'; // Convert to boolean
      } else {
        return String(value); // Convert to string
      }
    };

    // Use step options from state
    const optionsToUse = state.stepOptions;

    // Generate the final workflow configuration based on the current state
    const config = {
      graphFilter: {
        labelFilters: [],
        propertyFilters: [],
      },
      steps: [] as any[],
    };

    // Separate structured and unstructured entities
    const structuredEntities = state.entities.filter(
      (entity) => entity.entityType !== 'unstructured'
    );
    const unstructuredEntities = state.entities.filter(
      (entity) => entity.entityType === 'unstructured'
    );

    // Add IngestStructured step if there are structured entities with properties
    if (structuredEntities.length > 0) {
      const ingestStructuredStep = {
        type: 'IngestStructured',
        schema: {
          indexes: [] as any[],
          constraints: [] as any[],
        },
        files: [] as any[],
        relationships: [] as any[],
        options: {
          mergeNodes: optionsToUse?.['structured']?.mergeNodes ?? true,
          onDuplicate: optionsToUse?.['structured']?.onDuplicate ?? 'update',
          batchSize: optionsToUse?.['structured']?.batchSize ?? 0,
          vectorize: optionsToUse?.['structured']?.vectorize ?? false,
          encoding: optionsToUse?.['structured']?.encoding ?? 'utf-8',
        },
      };

      if (!ingestStructuredStep.options.mergeNodes) {
        delete (ingestStructuredStep.options as any).onDuplicate;
      }

      // Collect all properties by type across all structured entities
      const allDefaultIndexProperties: Record<string, string[]> = {};
      const allFulltextIndexPropertiesSet = new Set<string>();
      const allUniqueConstraintProperties: Record<string, string[]> = {};

      // Process each structured entity to collect properties
      structuredEntities.forEach((entity) => {
        const entityLabel = entity.name;

        // Initialize arrays for this entity if they don't exist
        if (!allDefaultIndexProperties[entityLabel]) {
          allDefaultIndexProperties[entityLabel] = [];
        }
        if (!allUniqueConstraintProperties[entityLabel]) {
          allUniqueConstraintProperties[entityLabel] = [];
        }

        // Process each property
        entity.properties.forEach((property) => {
          // Group properties by their index/constraint type
          if (property.hasIndex) {
            allDefaultIndexProperties[entityLabel].push(property.name);
          }

          // Use Set to automatically deduplicate property names for fulltext index
          if (property.isSearchable) {
            allFulltextIndexPropertiesSet.add(property.name);
          }

          if (property.isUnique) {
            allUniqueConstraintProperties[entityLabel].push(property.name);
          }
        });
      });

      // Create default indexes: one index object per property (not grouped per entity)
      Object.entries(allDefaultIndexProperties).forEach(
        ([entityLabel, properties]) => {
          properties.forEach((propertyName) => {
            ingestStructuredStep.schema.indexes.push({
              label: entityLabel,
              properties: [propertyName],
              type: 'default',
            });
          });
        }
      );

      // Create single consolidated fulltext index for all entities
      const allFulltextIndexProperties = Array.from(
        allFulltextIndexPropertiesSet
      );
      if (allFulltextIndexProperties.length > 0) {
        ingestStructuredStep.schema.indexes.push({
          labels: structuredEntities.map((entity) => entity.name),
          properties: allFulltextIndexProperties,
          type: 'fulltext',
          indexConfig: {
            'fulltext.analyzer': 'english',
            'fulltext.eventually_consistent': true,
          },
        });
      }

      // Create unique constraints for each entity
      Object.entries(allUniqueConstraintProperties).forEach(
        ([entityLabel, properties]) => {
          if (properties.length > 0) {
            ingestStructuredStep.schema.constraints.push({
              label: entityLabel,
              type: 'unique',
              properties: properties,
            });
          }
        }
      );

      // Process each structured entity to build files
      structuredEntities.forEach((entity) => {
        const entityLabel = entity.name;

        // Group properties by source file and create file entries
        const filesBySource: Record<string, any> = {};

        // Group properties by their source file
        // Include properties with sourceField OR location properties with latitudeColumn/longitudeColumn
        entity.properties
          .filter(
            (prop) =>
              prop.source &&
              (prop.sourceField ||
                (prop.type === 'location' &&
                  prop.latitudeColumn &&
                  prop.longitudeColumn))
          )
          .forEach((prop) => {
            if (!filesBySource[prop.source!]) {
              filesBySource[prop.source!] = {
                type: 'file',
                path: prop.source, // Use the storagePath directly
                fileEntryId: entity.fileEntryId || '',
                mappings: [],
              };
            }
          });

        // Add static properties for this entity
        const staticProperties: Record<string, any> = {};
        entity.properties
          .filter((prop) => prop.isStatic && prop.staticValue)
          .forEach((prop) => {
            staticProperties[prop.name] = {
              type: prop.type,
              value: convertStaticValue(prop.type, prop.staticValue),
            };
          });

        // Create mappings for each file
        Object.keys(filesBySource).forEach((sourceFile) => {
          const fileEntry = filesBySource[sourceFile];
          const propertiesMapping: Record<string, any> = {};

          // Get all properties for this source file
          // Include properties with sourceField OR location properties with latitudeColumn/longitudeColumn
          const fileProperties = entity.properties.filter(
            (prop) =>
              prop.source === sourceFile &&
              (prop.sourceField ||
                (prop.type === 'location' &&
                  prop.latitudeColumn &&
                  prop.longitudeColumn))
          );

          // Build properties mapping for this file
          fileProperties.forEach((property) => {
            // Handle location type properties - convert to geo format
            if (
              property.type === 'location' &&
              property.latitudeColumn &&
              property.longitudeColumn
            ) {
              propertiesMapping[property.name] = {
                type: 'geo',
                latColumn: property.latitudeColumn,
                lonColumn: property.longitudeColumn,
              };
              return;
            }

            // If encrypt is true, always use object format with type
            if (property.encrypt) {
              const propertyMapping: any = {
                column: property.sourceField,
                type: property.type,
                encrypt: true,
              };
              propertiesMapping[property.name] = propertyMapping;
            } else if (property.type === 'string') {
              // Simple string format when no encrypt and type is string
              propertiesMapping[property.name] = property.sourceField;
            } else {
              // Object format with type when type is not string
              propertiesMapping[property.name] = {
                type: property.type,
                column: property.sourceField,
              };
            }
          });

          // Create mapping for this file
          if (
            Object.keys(propertiesMapping).length > 0 ||
            Object.keys(staticProperties).length > 0
          ) {
            const mapping = {
              label: entityLabel,
              properties: propertiesMapping,
              staticProperties: staticProperties,
            };

            fileEntry.mappings.push(mapping);
            ingestStructuredStep.files.push(fileEntry);
          }
        });

        // Handle entities with only static properties (no file mappings)
        if (
          Object.keys(staticProperties).length > 0 &&
          Object.keys(filesBySource).length === 0
        ) {
          const staticFileEntry = {
            type: 'file',
            path: `data/projects/${
              selectedProjectName || 'default'
            }/static_${entityLabel.toLowerCase()}.json`,
            fileEntryId: entity.fileEntryId || '',
            mappings: [
              {
                label: entityLabel,
                properties: {},
                staticProperties: staticProperties,
              },
            ],
          };
          ingestStructuredStep.files.push(staticFileEntry);
        }
      });

      // Add relationships if any
      if (state.relationships.length > 0) {
        state.relationships.forEach((relationship) => {
          const relationshipStep = {
            type: 'file',
            path: relationship.file || '',
            name: relationship.name,
            startLabel: relationship.sourceEntity,
            startSourceField: relationship.sourceProperty,
            startNodeProperty: relationship.sourceProperty,
            endLabel: relationship.targetEntity,
            endSourceField: relationship.targetProperty,
            endNodeProperty: relationship.targetProperty,
            properties: {} as Record<string, any>,
            staticProperties: {} as Record<string, any>,
          };

          // Add relationship properties
          relationship.properties.forEach((prop) => {
            if (prop.sourceField) {
              relationshipStep.properties[prop.name] = prop.sourceField;
            } else if (prop.isStatic && prop.staticValue) {
              relationshipStep.staticProperties[prop.name] = {
                type: prop.type,
                value: convertStaticValue(prop.type, prop.staticValue),
              };
            }
          });

          ingestStructuredStep.relationships.push(relationshipStep);
        });
      }

      // Add relationship mappings if any
      if (state.relationshipMappings.length > 0) {
        state.relationshipMappings.forEach((mapping) => {
          const relationshipStep = {
            type: 'file',
            path: mapping.file, // Use the file path directly
            fileEntryId: mapping.fileEntryId,
            name: mapping.name,
            startLabel: mapping.startLabel,
            startSourceField: mapping.startSourceField,
            startNodeProperty: mapping.startNodeProperty || 'id',
            endLabel: mapping.endLabel,
            endSourceField: mapping.endSourceField,
            endNodeProperty: mapping.endNodeProperty || 'id',
            properties: {} as Record<string, any>,
            staticProperties: {} as Record<string, any>,
          };

          // Add relationship properties
          mapping.properties.forEach((prop) => {
            if (prop.sourceField) {
              relationshipStep.properties[prop.name] = prop.sourceField;
            } else if (prop.isStatic && prop.staticValue) {
              relationshipStep.staticProperties[prop.name] = {
                type: prop.type,
                value: convertStaticValue(prop.type, prop.staticValue),
              };
            }
          });

          ingestStructuredStep.relationships.push(relationshipStep);
        });
      }

      config.steps.push(ingestStructuredStep);
    }

    // Add IngestUnstructured step if there are unstructured entities
    if (unstructuredEntities.length > 0) {
      // Group unstructured entities by file
      const filesByPath: Record<string, any> = {};

      unstructuredEntities.forEach((entity) => {
        const isDirectory = entity.fileType === 'directory';
        const candidatePaths = (() => {
          if (isDirectory && entity.filePath) {
            return [entity.filePath];
          }

          if (entity.filePaths && entity.filePaths.length > 0) {
            return entity.filePaths;
          }

          return entity.filePath ? [entity.filePath] : [];
        })();

        const uniquePaths = Array.from(
          new Set(
            candidatePaths.filter((path): path is string => Boolean(path))
          )
        );

        // Handle multiple files with different fileEntryIds
        const fileEntryIds =
          entity.fileEntryIds ||
          (entity.fileEntryId ? [entity.fileEntryId] : []);

        if (uniquePaths.length === 0) {
          return;
        }

        const shouldAddFilename =
          isDirectory || (entity.filePaths?.length || 0) > 1;

        const ensureFileEntry = (path: string, fileEntryId?: string) => {
          if (!filesByPath[path]) {
            // Extract agent configuration with readable variable names; map legacy Azure types to LangChain
            const defaultAgentType = getDefaultAgentTypeForFileType(
              entity.fileType || 'pdf'
            );
            const agentType = extractionAgentTypeFromApi(
              entity.extractionSettings?.agent?.type,
              defaultAgentType
            );
            const loaderDefaults = getDefaults(
              SERVICES.UNSTRUCTURED_DATA_LOADER,
              agentType
            );
            const modelName =
              entity.extractionSettings?.agent?.modelName ||
              loaderDefaults.modelName;
            const modelVersion =
              entity.extractionSettings?.agent?.modelVersion ||
              loaderDefaults.modelVersion;

            // Filter out 'all' entries from filePages - if all entries are 'all', don't include filePages
            const filePages = entity.extractionSettings?.filePages;
            const filteredFilePages = filePages
              ? Object.entries(filePages).reduce((acc, [filePath, value]) => {
                  if (value !== 'all') {
                    acc[filePath] = value;
                  }
                  return acc;
                }, {} as Record<string, string | string[]>)
              : undefined;
            const shouldIncludeFilePages =
              filteredFilePages && Object.keys(filteredFilePages).length > 0;

            filesByPath[path] = {
              path,
              fileEntryId: fileEntryId || entity.fileEntryId || '',
              type: isDirectory ? 'directory' : 'file',
              fileTypes: isDirectory
                ? entity.directoryFileTypes &&
                  entity.directoryFileTypes.length > 0
                  ? entity.directoryFileTypes
                  : ['pdf']
                : undefined,
              addFilenameAsProperty: shouldAddFilename,
              nodes: [],
              relationships: [],
              extractionRule: {
                agent: {
                  type: agentType,
                  modelName: modelName,
                  modelVersion: modelVersion,
                  extractionPasses:
                    entity.extractionSettings?.agent?.extractionPasses || 2,
                  targetFormat: entity.extractionSettings?.agent?.targetFormat,
                  prompt: entity.extractionSettings?.agent?.prompt,
                },
                findUnspecifiedRelationships:
                  entity.extractionSettings?.findUnspecifiedRelationships ||
                  false,
                knownEntities: entity.extractionSettings?.knownEntities || {},
                ...(shouldIncludeFilePages && { filePages: filteredFilePages }),
              },
            };
          } else {
            if (shouldAddFilename && !filesByPath[path].addFilenameAsProperty) {
              filesByPath[path].addFilenameAsProperty = true;
            }

            if (
              isDirectory &&
              entity.directoryFileTypes &&
              entity.directoryFileTypes.length > 0 &&
              !filesByPath[path].fileTypes
            ) {
              filesByPath[path].fileTypes = entity.directoryFileTypes;
            }
          }

          return filesByPath[path];
        };

        // Add node configuration for this entity
        const nodeConfig = {
          label: entity.name,
          properties: entity.properties
            .filter((prop) => !prop.isStatic || prop.name === 'file_path')
            .map((prop) => {
              const propertyConfig: any = {
                type: prop.type,
                property: prop.name,
              };

              // Handle location type properties - convert to geo format
              // Only convert if required coordinate fields are present
              if (prop.type === 'location') {
                // For unstructured entities, use latProperty/lonProperty (new format) or
                // latColumn/lonColumn (legacy format) for backward compatibility
                // For structured entities, use latColumn/lonColumn
                const hasRequiredFields =
                  entity.entityType === 'unstructured'
                    ? // Accept both new format (latitudeProperty/longitudeProperty) and legacy format
                      // (latitudeColumn/longitudeColumn) for backward compatibility
                      (prop.latitudeProperty && prop.longitudeProperty) ||
                      (prop.latitudeColumn && prop.longitudeColumn)
                    : prop.latitudeColumn && prop.longitudeColumn;

                if (hasRequiredFields) {
                  propertyConfig.type = 'geo';
                  if (entity.entityType === 'unstructured') {
                    // Prefer new format (latitudeProperty/longitudeProperty), fallback to legacy
                    // (latitudeColumn/longitudeColumn) for backward compatibility
                    if (prop.latitudeProperty && prop.longitudeProperty) {
                      propertyConfig.latProperty = prop.latitudeProperty;
                      propertyConfig.lonProperty = prop.longitudeProperty;
                    } else if (prop.latitudeColumn && prop.longitudeColumn) {
                      // Legacy format: use latColumn/lonColumn as latProperty/lonProperty
                      propertyConfig.latProperty = prop.latitudeColumn;
                      propertyConfig.lonProperty = prop.longitudeColumn;
                    }
                  } else {
                    // Structured entities use latColumn/lonColumn
                    propertyConfig.latColumn = prop.latitudeColumn;
                    propertyConfig.lonColumn = prop.longitudeColumn;
                  }
                }
                // If required fields are missing, keep original type 'location'
                // This prevents creating invalid geo properties without coordinates
              }

              if (prop.required) {
                propertyConfig.required = true;
              }

              if (prop.encrypt) {
                propertyConfig.encrypt = true;
              }

              return propertyConfig;
            }),
          staticProperties: {} as Record<string, any>,
          extractInstructions: entity.extractInstructions || [],
          links: [],
          labelConstrains: entity.labelConstraints || [],
        };

        entity.properties
          .filter(
            (prop) =>
              prop.isStatic && prop.staticValue && prop.name !== 'file_path'
          )
          .forEach((prop) => {
            nodeConfig.staticProperties[prop.name] = {
              type: prop.type,
              value: convertStaticValue(prop.type, prop.staticValue),
            };
          });

        uniquePaths.forEach((path, index) => {
          const fileEntryId = fileEntryIds[index] || entity.fileEntryId || '';
          const entry = ensureFileEntry(path, fileEntryId);
          entry.nodes.push(nodeConfig);
        });
      });

      // Add unstructured relationships to files
      if (state.unstructuredRelationships.length > 0) {
        state.unstructuredRelationships.forEach((relationship) => {
          // Find the file entry that matches this relationship's file path
          const fileEntry = filesByPath[relationship.filePath];
          if (fileEntry) {
            // Check if this relationship already exists in the file
            const existingRelationship = fileEntry.relationships.find(
              (rel: any) =>
                rel.name === relationship.name &&
                rel.startLabel === relationship.startLabel &&
                rel.endLabel === relationship.endLabel
            );

            if (!existingRelationship) {
              fileEntry.relationships.push({
                name: relationship.name,
                startLabel: relationship.startLabel,
                endLabel: relationship.endLabel,
                staticProperties: relationship.staticProperties || {},
              });
            }
          }
        });
      }

      // Collect all properties by type across all unstructured entities
      const allUnstructuredDefaultIndexProperties: Record<string, string[]> =
        {};
      const allUnstructuredFulltextIndexProperties: string[] = [];
      const allUnstructuredUniqueConstraintProperties: Record<
        string,
        string[]
      > = {};

      // Process each unstructured entity to collect properties
      unstructuredEntities.forEach((entity) => {
        const entityLabel = entity.name;

        // Initialize arrays for this entity if they don't exist
        if (!allUnstructuredDefaultIndexProperties[entityLabel]) {
          allUnstructuredDefaultIndexProperties[entityLabel] = [];
        }
        if (!allUnstructuredUniqueConstraintProperties[entityLabel]) {
          allUnstructuredUniqueConstraintProperties[entityLabel] = [];
        }

        // Process each property
        entity.properties.forEach((property) => {
          // Group properties by their index/constraint type
          if (property.hasIndex) {
            allUnstructuredDefaultIndexProperties[entityLabel].push(
              property.name
            );
          }

          if (property.isSearchable) {
            allUnstructuredFulltextIndexProperties.push(property.name);
          }

          if (property.isUnique) {
            allUnstructuredUniqueConstraintProperties[entityLabel].push(
              property.name
            );
          }
        });
      });

      // Create IngestUnstructured step
      const ingestUnstructuredStep = {
        type: 'IngestUnstructured',
        schema: {
          indexes: [] as any[],
          constraints: [] as any[],
        },
        files: Object.values(filesByPath),
        options: {
          mergeNodes: optionsToUse?.['unstructured']?.mergeNodes ?? true,
          onDuplicate: optionsToUse?.['unstructured']?.onDuplicate ?? 'update',
          batchSize: optionsToUse?.['unstructured']?.batchSize ?? 0,
          vectorize: optionsToUse?.['unstructured']?.vectorize ?? true,
          encoding: optionsToUse?.['unstructured']?.encoding ?? 'utf-8',
        },
        deleteExtractData: false,
      };

      if (!ingestUnstructuredStep.options.mergeNodes) {
        delete (ingestUnstructuredStep.options as any).onDuplicate;
      }

      // Create default indexes for each unstructured entity
      Object.entries(allUnstructuredDefaultIndexProperties).forEach(
        ([entityLabel, properties]) => {
          if (properties.length > 0) {
            ingestUnstructuredStep.schema.indexes.push({
              label: entityLabel,
              properties: properties,
              type: 'default',
            });
          }
        }
      );

      // Create single consolidated fulltext index for all unstructured entities
      if (allUnstructuredFulltextIndexProperties.length > 0) {
        ingestUnstructuredStep.schema.indexes.push({
          labels: unstructuredEntities.map((entity) => entity.name),
          properties: allUnstructuredFulltextIndexProperties,
          type: 'fulltext',
          indexConfig: {
            'fulltext.analyzer': 'english',
            'fulltext.eventually_consistent': true,
          },
        });
      }

      // Create unique constraints for each unstructured entity
      Object.entries(allUnstructuredUniqueConstraintProperties).forEach(
        ([entityLabel, properties]) => {
          if (properties.length > 0) {
            ingestUnstructuredStep.schema.constraints.push({
              label: entityLabel,
              type: 'unique',
              properties: properties,
            });
          }
        }
      );

      config.steps.push(ingestUnstructuredStep);
    }

    // Add LinkNodes steps if there are configurations
    if (state.linkNodesConfigurations.length > 0) {
      state.linkNodesConfigurations.forEach((configuration) => {
        // Remove the internal id field from the final workflow JSON
        const linkNodesStep = { ...configuration } as any;
        delete linkNodesStep.id;

        // Process linkRules to remove strategy when method is 'none'
        if (linkNodesStep.link?.linkRules) {
          linkNodesStep.link.linkRules.forEach((rule: any) => {
            if (rule.strategy?.method === 'none') {
              delete rule.strategy;
            }
          });
        }

        // Process pipeline strategies to ensure all required fields are included
        if (linkNodesStep.link?.pipeline) {
          // Sort pipeline strategies in the correct order before processing
          linkNodesStep.link.pipeline = sortPipelineStrategies(
            linkNodesStep.link.pipeline
          );

          linkNodesStep.link.pipeline = linkNodesStep.link.pipeline.map(
            (strategy: any) => {
              const processedStrategy: any = {
                method: strategy.method,
              };

              // Add method-specific required fields
              switch (strategy.method) {
                case 'exact_match':
                  processedStrategy.threshold = strategy.threshold ?? 1.0;
                  break;
                case 'jaccard_similarity':
                  processedStrategy.threshold = strategy.threshold ?? 0.8;
                  if (strategy.token_separator) {
                    processedStrategy.token_separator =
                      strategy.token_separator;
                  }
                  break;
                case 'embedding_similarity':
                  processedStrategy.threshold = strategy.threshold ?? 0.8;
                  processedStrategy.minhashThreshold =
                    strategy.minhashThreshold ?? 0.25;
                  processedStrategy.agent = {
                    type:
                      strategy.agent?.type ??
                      EMBEDDINGS_API_TYPES.EMBEDDINGS_API,
                    model: strategy.agent?.model ?? embeddingDefaultModel,
                    dimensions: strategy.agent?.dimensions ?? 3072,
                  };
                  break;
                case 'llm':
                  processedStrategy.minhashThreshold =
                    strategy.minhashThreshold ?? 0.25;
                  processedStrategy.agent = {
                    type: strategy.agent?.type ?? LINKING_AGENT_TYPES.LANGCHAIN,
                    modelName: strategy.agent?.modelName ?? linkingDefaultModel,
                    temperature: strategy.agent?.temperature ?? 0.0,
                  };
                  // Only add instructions if they exist
                  if (strategy.agent?.instructions) {
                    processedStrategy.agent.instructions =
                      strategy.agent.instructions;
                  }
                  break;
              }

              return processedStrategy;
            }
          );
        }

        config.steps.push(linkNodesStep);
      });
    }

    // Add Enrich steps if there are enrich rules
    if (state.enrichRules.length > 0) {
      state.enrichRules.forEach((enrichRule) => {
        const enrichStep = {
          type: 'Enrich',
          rule: enrichRule,
        };
        config.steps.push(enrichStep);
      });
    }

    // Add CypherClassifier steps
    if (state.cypherClassifierSteps.length > 0) {
      state.cypherClassifierSteps.forEach((step) => {
        config.steps.push({ ...step });
      });
    }

    return config;
  };

  const value: WorkflowsContextType = {
    state,
    isReadonly,
    addEntity,
    updateEntity,
    removeEntity,
    addRelationship,
    updateRelationship,
    removeRelationship,
    addRelationshipMapping,
    updateRelationshipMapping,
    removeRelationshipMapping,
    addUnstructuredRelationship,
    updateUnstructuredRelationship,
    removeUnstructuredRelationship,
    addLinkNodesConfiguration,
    updateLinkNodesConfiguration,
    removeLinkNodesConfiguration,
    addEnrichRule,
    updateEnrichRule,
    removeEnrichRule,
    setCurrentStep,
    resetWorkflow,
    loadWorkflow,
    getMetrics,
    updateStepOptions,
    generateWorkflowConfig,
    setIsReadonly: setIsReadonlyState,
  };

  return (
    <WorkflowsContext.Provider value={value}>
      {children}
    </WorkflowsContext.Provider>
  );
};
